type TrackPublisher = {
  setTrackOnAll(kind: 'audio' | 'video', track: MediaStreamTrack): Promise<void>;
  requestKeyframeForAllPeers(): void;
};

/** Serializes sender changes and lets source selection await the same work as media events. */
export class TrackPublicationManager {
  private tail: Promise<void> = Promise.resolve();
  private pending = new Map<string, { track: MediaStreamTrack; update: Promise<void> }>();
  private keyframeTimer: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  constructor(private readonly publisher: TrackPublisher) {}

  public publish(track: MediaStreamTrack): Promise<void> {
    const existing = this.pending.get(track.kind);
    if (existing?.track === track) return existing.update;
    const update = this.tail.then(async () => {
      if (this.destroyed) throw new Error('Track publisher is closed');
      await this.publisher.setTrackOnAll(track.kind as 'audio' | 'video', track);
      if (this.destroyed) return;
      clearTimeout(this.keyframeTimer);
      this.keyframeTimer = setTimeout(() => this.publisher.requestKeyframeForAllPeers(), 300);
    });
    this.pending.set(track.kind, { track, update });
    const settled = () => {
      if (this.pending.get(track.kind)?.update === update) this.pending.delete(track.kind);
    };
    this.tail = update.then(settled, settled);
    return update;
  }

  public destroy(): void {
    this.destroyed = true;
    clearTimeout(this.keyframeTimer);
  }
}
